import {
  Alert,
  Box,
  Button,
  FormControl,
  FormGroup,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export const AuthContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: 800,
});

export const AuthFormGroup = styled(FormGroup)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const AuthFieldControl = styled(FormControl)(() => ({
  width: '100%',
}));

export const AuthFormHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3.5),
  [theme.breakpoints.up('xl')]: {
    marginBottom: theme.spacing(4.5),
  },
}));

export const AuthFormTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  lineHeight: 1.5,
  letterSpacing: 0,
  '&&': {
    fontSize: 26,
    fontWeight: 700,
    [theme.breakpoints.up('sm')]: { fontSize: 30 },
    [theme.breakpoints.up('xl')]: { fontSize: 36 },
    [theme.breakpoints.up('xxl')]: { fontSize: 42 },
  },
}));

export const AuthFormSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  '&&': {
    fontSize: 15,
    [theme.breakpoints.up('xl')]: { fontSize: 17 },
    [theme.breakpoints.up('xxl')]: { fontSize: 19 },
  },
}));

export const AuthPasswordConditions = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const AuthForgotPasswordLinkContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
}));

export const AuthNavigationLink = styled(Link)(({ theme }) => ({
  padding: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  cursor: 'pointer',
  '&:disabled': {
    color: theme.palette.text.disabled,
    cursor: 'default',
  },
}));

export const AuthTopControls = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

export const AuthCloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[500],
}));

export const AuthFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  '& input::-ms-reveal, & input::-ms-clear': { display: 'none' },
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 2.5,
    backgroundColor: alpha(theme.palette.text.primary, 0.025),
    transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
    '& fieldset': {
      borderColor: alpha(theme.palette.text.primary, 0.12),
      transition: 'border-color 0.2s ease',
    },
    '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
    '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.4) },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.common,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
    [theme.breakpoints.up('xl')]: { fontSize: '1.0625rem' },
    [theme.breakpoints.up('xxl')]: { fontSize: '1.15rem' },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': { color: theme.palette.primary.main },
    [theme.breakpoints.up('xl')]: { fontSize: '1.0625rem' },
    [theme.breakpoints.up('xxl')]: { fontSize: '1.15rem' },
  },
}));

export const AuthFormContent = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const AuthErrorAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  border: `1px solid ${alpha(theme.palette.error.main, 0.32)}`,
  borderRadius: theme.shape.borderRadius * 2.5,
  color: theme.palette.error.dark,
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  alignItems: 'center',
  '& .MuiAlert-icon': { color: theme.palette.error.main },
  '& .MuiAlert-message': { fontWeight: 600 },
}));

export const AuthSubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  borderRadius: theme.shape.borderRadius * 2.5,
  fontSize: 15,
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  backgroundColor: theme.palette.primary.main,
  transition: 'background-color 0.2s ease, opacity 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    opacity: 0.7,
  },
  [theme.breakpoints.up('xl')]: {
    paddingTop: theme.spacing(1.6),
    paddingBottom: theme.spacing(1.6),
    fontSize: 17,
  },
  [theme.breakpoints.up('xxl')]: {
    paddingTop: theme.spacing(1.9),
    paddingBottom: theme.spacing(1.9),
    fontSize: 19,
  },
}));

export const PageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100dvh',
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'safe center',
  justifyContent: 'safe center',
  padding: theme.spacing(10, 6, 4),
  boxSizing: 'border-box',
  overflowX: 'hidden',
  overflowY: 'auto',
  scrollbarGutter: 'stable',
  overscrollBehaviorY: 'contain',
  backgroundColor: theme.palette.primary.dark,
  backgroundImage: `linear-gradient(160deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(9, 2.5, 3),
  },
  [theme.breakpoints.up('lg')]: {
    display: 'grid',
    height: 'auto',
    alignItems: 'stretch',
    justifyContent: 'stretch',
    gridTemplateColumns: 'minmax(420px, 0.98fr) minmax(460px, 1.02fr)',
    padding: 0,
    overflowY: 'visible',
    scrollbarGutter: 'auto',
    overscrollBehaviorY: 'auto',
    backgroundColor: theme.palette.background.common,
    backgroundImage: 'none',
  },
}));

export const FormPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  width: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.up('lg')]: {
    alignItems: 'stretch',
    order: 2,
    minHeight: '100vh',
    padding: theme.spacing(5, 8),
    backgroundColor: theme.palette.background.common,
  },
}));

export const TopControls = styled(Box)(({ theme }) => ({
  minHeight: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 16,
  [theme.breakpoints.down('lg')]: {
    position: 'absolute',
    zIndex: 2,
    top: theme.spacing(4),
    right: theme.spacing(4),
    '& .MuiSelect-root, & .MuiSelect-select, & .MuiSvgIcon-root': {
      color: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.common.white, 0.42),
    },
  },
  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(2),
    right: theme.spacing(1),
  },
}));

export const MobileLogo = styled('img')(({ theme }) => ({
  display: 'block',
  width: 150,
  maxWidth: '60%',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('sm')]: { width: 170 },
  [theme.breakpoints.up('lg')]: { display: 'none' },
}));

export const FormContainer = styled(Box)(({ theme }) => ({
  flex: '0 0 auto',
  height: 'auto',
  width: '100%',
  maxWidth: 560,
  margin: 'auto',
  boxSizing: 'border-box',
  padding: theme.spacing(5.75),
  borderRadius: theme.shape.borderRadius * 6,
  backgroundColor: theme.palette.background.common,
  boxShadow: `0 22px 60px ${alpha(theme.palette.common.black, 0.22)}`,
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(3.5) },
  [theme.breakpoints.up('lg')]: {
    flex: 1,
    maxWidth: 400,
    margin: '0 auto',
    boxSizing: 'content-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
    borderRadius: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  [theme.breakpoints.up('xl')]: { maxWidth: 480 },
  [theme.breakpoints.up('xxl')]: { maxWidth: 540 },
}));

export const CardInfoAccordion = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3.5),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
  [theme.breakpoints.up('lg')]: { display: 'none' },
}));

export const DeviceIdentifiersFooter = styled(Box)(({ theme }) => ({
  flex: '0 0 auto',
  width: '100%',
  marginTop: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: alpha(theme.palette.common.white, 0.62),
  '& > div': {
    marginTop: 0,
    justifyContent: 'center',
  },
  [theme.breakpoints.up('lg')]: { display: 'none' },
}));

export const VersionText = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.common.white, 0.62),
  fontSize: 12,
  lineHeight: 1.4,
  marginTop: theme.spacing(1),
  textAlign: 'center',
  '&.cardVersion': {
    marginTop: theme.spacing(2.5),
    color: theme.palette.text.info,
  },
  '&.desktopVersion': { display: 'none' },
  [theme.breakpoints.up('lg')]: {
    marginTop: 0,
    color: theme.palette.text.info,
    '&.cardVersion': { display: 'none' },
    '&.desktopVersion': { display: 'block' },
  },
}));

export const InformationPanel = styled(Box)(({ theme }) => ({
  color: theme.palette.common.white,
  display: 'none',
  backgroundColor: theme.palette.primary.dark,
  backgroundImage: `linear-gradient(160deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    flexDirection: 'column',
    order: 1,
    minHeight: '100vh',
    padding: 'clamp(24px, 2.5vw, 48px) clamp(48px, 3.333vw, 64px) clamp(20px, 2.083vw, 40px)',
    borderRight: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  },
}));

export const DesktopLogo = styled('img')(({ theme }) => ({
  maxWidth: '70%',
  display: 'none',
  filter: 'brightness(0) invert(1)',
  [theme.breakpoints.up('lg')]: {
    display: 'block',
    width: 'clamp(220px, calc(13.889vw + 53.33px), 320px)',
  },
}));

export const InformationContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 'clamp(28px, 2.083vw, 40px)',
  [theme.breakpoints.up('lg')]: {
    paddingBottom: '5vh',
    '&:has(details[open])': {
      transform: 'translateY(clamp(16px, 2vh, 28px))',
    },
  },
}));

export const PanelIntroduction = styled(Box)(() => ({
  width: '90%',
  maxWidth: 700,
}));

export const PanelTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  '&&': {
    width: '100%',
    marginBottom: 'clamp(16px, 1.25vw, 24px)',
    fontSize: 'clamp(32px, 2.4vw, 52px)',
    fontWeight: 700,
    lineHeight: 1.16,
    letterSpacing: 0,
  },
}));

export const PanelText = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.common.white, 0.82),
  '&&': {
    maxWidth: 'clamp(420px, calc(36.111vw - 13.33px), 680px)',
    fontSize: 'clamp(15px, calc(0.556vw + 8.33px), 19px)',
    lineHeight: 1.65,
  },
}));

export const RegulatoryInformationContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: 'clamp(500px, calc(25vw + 200px), 680px)',
}));
